/* eslint-disable complexity */
/* eslint-disable max-lines */
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import {
  getCollection,
  getCollectionHandle,
  getRawCollection,
  withTransaction,
} from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { Price } from '@adornis/payments/db/prices.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { uniqueEntries } from '@adornis/validation/functions/uniqueEntires.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { sendCertificate } from '@ils/certificates/api/certificates.js';
import { maxCandidatesResolver } from '@ils/examdays/server/resolver.js';
import { Location } from '@ils/locations/db/location.js';
import { OSCE } from '@ils/osces/db/osce.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { hasGlobalRole } from '@ils/permissions/hasRole.js';
import type { ScoreStatus } from '@ils/ratings/db/score.js';
import { Slot } from '@ils/slots/db/slot.js';
import { ExamDayRatingType, ExamType, UserRole } from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import { Observable, from, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { sendRatingResultMail } from '../server/resolver/examDay.js';
import { createTestExam } from '../server/test-exam-appointment/create-test-exam.js';
import { generateCertificatePdfFileName } from '../standalone-components/PdfUtil.js';
import { resolveSubscribeActiveReservationsByExamDayCount } from './examday-reservation.js';
import { ExamDayFilter } from './filter/examday-filter.js';
import { ILSProduct } from './ils-product.js';
import { ILSUser } from './ils-user.js';
import { Order, getOrdersByExamDayAndTrainees } from './order.js';

enum TimelimeItemType {
  ROUND = 'round',
  LUNCH_PAUSE = 'lunch-pause',
  BLOCK_PAUSE = 'block-pause',
}

interface TimelineItem {
  type: TimelimeItemType;
  start: number;
  end: number;
}

export interface Round {
  round: number;
  timeline: TimelineItem;
  fallbackTesterID: string | null;
  info: RoundInfo[];
}

export interface RoundInfo {
  osceID: string;
  testerID: Maybe<string>;
  allowed: boolean;
}

const processMap: Record<string, Observable<string[]>> = {};

@Entity()
export class OsceTesterTuple extends AdornisEntity {
  public static override _class = 'OsceTesterTuple';

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @Field(type => String)
  osceID!: string;

  @Field(type => OSCE, {
    resolve: function (this: OsceTesterTuple) {
      return async (gqlFields: BaseQLSelectionSet<OSCE>) => {
        if (!this.osceID) return null;
        const osce = await loadMongoData(OSCE, this.osceID);
        if (osce) return osce;

        const col = await getRawCollection<OSCE>(OSCE._collectionName);
        const publicOSCE = await col.findOne(
          { _id: this.osceID },
          { projection: { _id: 1, _class: 1, titleTrainee: 1, osceType: 1, taskType: 1, durationInMinutes: 1 } },
        );
        return publicOSCE as OSCE;
      };
    },
  })
  osce: Maybe<OSCE>;

  @Field(type => ID)
  osceGroupID: Maybe<string>;

  @validate(({ value, key, target }) => {
    if (target.osceGroupID && typeof value !== 'number')
      throw new ValidationError('OsceTesterTuple muss ein osceGroupIndex (number) beinhalten', { key, target });
  })
  @Field(type => Int)
  osceGroupIndex: Maybe<number>;

  @Field(type => [String], { default: v => v ?? [] })
  testerIDs!: Array<string | null>;

  constructor(doc: PartialEntityData<OsceTesterTuple>) {
    super(doc);
  }
}

/**
 * @property {Date} start - date of exam begin
 * @property {number} dayLength - length of exam day
 * @property {Date} pauseStart - starting time of lunch break
 * @property {number} pauseLength - length of lunch break
 * @property {string[]} candidateIDs - id list of all candidates for the exam
 * @property {ExamDayReservation[]} reservations - list of active reservations for the exam day
 * @property {string[]} modules - id list for related modules with exam
 * @property {number[]} moduleLengths - length in minutes for each respective module
 * @property {Int} slotPause - pause between slots in minutes
 */
@Entity()
export class ExamDay extends ILSProduct {
  public static override _class = 'ExamDay';

  static override get allFields() {
    return { ...selectionSet(() => ExamDay, 4, ['candidates']), slots: Slot.allFields };
  }

  constructor(doc: PartialEntityData<ExamDay>) {
    super(doc);
  }

  @Field(type => ID)
  locationID: Maybe<string>;
  @DataLoadedField(type => Location, ({ locationID }) => locationID)
  location: Maybe<Location>;

  @Field(type => String)
  examType: Maybe<ExamType>;

  @validate(nonOptional())
  @Field(type => DateTime)
  start!: DateTime;

  @validate(
    and(nonOptional(), ({ value, target, key }) => {
      if (value <= target.start)
        throw new ValidationError('End-Zeit des Prüfungstags muss nach der Start-Zeit liegen', { key, target });
    }),
  )
  @Field(type => DateTime)
  end!: DateTime;

  @Field(type => [String], { default: v => v ?? [] })
  slotIDs!: string[];

  @DataLoadedField(type => [Slot], (examDay: ExamDay) => examDay.slotIDs)
  slots!: Slot[];

  @Field(type => Int, {
    default: v => v ?? 0,
    resolve: function (this: ExamDay) {
      return resolveSubscribeActiveReservationsByExamDayCount(this._id);
    },
  })
  activeReservationsCount!: number;

  @Field(type => [String], {
    resolve(this: ExamDay) {
      return async (gqlFields: BaseQLSelectionSet<ExamDay>) => {
        const col = await getRawCollection<Slot>(Names.Slot._collectionName);
        const results = await col
          .find({
            candidateID: { $in: this.candidateIDs },
            endTime: { $lt: DateTime.now().toJSDate() },
            isCandidateMissing: { $ne: true },
          })
          .project({ osceID: 1 })
          .toArray();
        const uniqueSet = new Set(results.map(r => r.osceID));
        return Array.from(uniqueSet);
      };
    },
  })
  participatedOSCEs: Maybe<string[]>;

  // Own Fields

  /**
   * setting this `0` will result in no break while scheduling
   */
  @validate(({ value, key, target }: ValidationFunctionOptions<number, ExamDay>) => {
    if (value < 0) throw new ValidationError('Die Pause kann nicht negativ sein.', { key, instance: target });
  })
  @Field(type => Int, { default: v => v ?? 3 })
  breakAfterRounds!: number;

  @validate(({ value, key, target }: ValidationFunctionOptions<number, ExamDay>) => {
    if (value < 15)
      throw new ValidationError('Mittagspause muss mindestens 15 Minuten lang sein.', {
        key,
        instance: target,
      });
  })
  @Field(type => Int, { default: v => v ?? 30 })
  breakInMinutes!: number;

  @Field(type => Int, { default: v => v ?? 1 })
  slotPause!: number;

  @Field(type => Int, { default: v => v ?? 20 })
  blockPause!: number;

  @Field(type => [ID], { default: v => v ?? [] })
  testers?: string[];

  @Field(type => [ID], { default: v => v ?? [] })
  fallbackTesters!: Array<string | null>;

  /**
   * this is only for ordering, the assignment is done via orders that have the respective product (examday)
   */
  @validate(uniqueEntries())
  @Field(type => [ID], { default: v => v ?? [] })
  candidateIDs!: string[];

  @DataLoadedField(type => [ILSUser], ({ candidateIDs }) => candidateIDs)
  candidates?: ILSUser[];

  @Field(type => [ID], { default: v => v ?? [] })
  examCenter?: Maybe<string[]>;

  @Field(type => [ID], { default: v => v ?? [] })
  osceTemplates?: string[];

  @validate(({ value, key, target }: ValidationFunctionOptions<OsceTesterTuple[], ExamDay>) => {
    if (
      Math.max(
        ...Object.values(
          value.reduce<{ [key: string]: number }>((acc, curr) => {
            if (!curr.osceGroupID) return acc;
            const key = `${curr.osceGroupID}_${curr.osceGroupIndex}`;
            if (!acc[key]) acc[key] = 1;
            else acc[key] += 1;
            return acc;
          }, {}),
        ),
      ) > 1
    )
      throw new ValidationError('Es dürfen keine doppelten Indizes für eine OSCE-Gruppe vorkommen', { key, target });
  })
  @Field(type => [OsceTesterTuple], { default: v => v ?? [] })
  osceTesterTuples!: OsceTesterTuple[];

  @Field(type => Boolean, { default: v => v ?? false })
  isPublishedForTesters: Maybe<boolean>;

  // using resolver to avoid temptation to save this field or depend on this field in the DB
  @Field(type => Int, {
    resolve(this: ExamDay) {
      return async () => this.numRatedUsers;
    },
  })
  numRatedUsers: Maybe<number>;

  // using resolver to avoid temptation to save this field or depend on this field in the DB
  @Field(type => Int, {
    resolve(this: ExamDay) {
      return async () => this.numParticipatedUsers;
    },
  })
  numParticipatedUsers: Maybe<number>;

  // using resolver to avoid temptation to save this field or depend on this field in the DB
  @Field(type => Int, {
    resolve(this: ExamDay) {
      return async () => this.numPassedUsers;
    },
  })
  numPassedUsers: Maybe<number>;

  @Field(type => Int, {
    resolve(this: ExamDay | EntityData<ExamDay>) {
      return maxCandidatesResolver(this);
    },
  })
  public maxCandidates!: number;

  @validate(
    and(nonOptional(), ({ value, key, target }) => {
      if (!Object.values(ExamDayRatingType).includes(value))
        throw new ValidationError(
          'Prüfungstag-Typ muss einem der folgenden Werte entsprechen: ' + Object.values(ExamDayRatingType).join(', '),
          { key, target },
        );
    }),
  )
  @Field(type => String, { default: v => v ?? ExamDayRatingType.FAMED })
  ratingType: Maybe<ExamDayRatingType>;

  override async save() {
    this.generateTesterArrays();
    return super.save();
  }

  override async remove() {
    if (this.candidateIDs.length)
      throw new Error('remove(): Das Löschen von ExamDays mit Buchungen ist nicht möglich.');

    return super.remove();
  }

  static override beforeCreate(entity: ExamDay): Promise<void> {
    entity.name = this.generateName(entity);

    return super.beforeCreate(entity);
  }

  static override async transformBeforeUpdate(oldEntity: EntityData<ExamDay>, update: ExamDay): Promise<void> {
    this.setName(oldEntity, update);

    return super.transformBeforeUpdate(oldEntity, update);
  }

  private static setName(oldEntity: EntityData<ExamDay>, update: ExamDay) {
    if (update.has('start') || update.has('end')) {
      if (!update.has('start')) update.start = DateTime.fromJSDate(oldEntity.start);
      if (!update.has('end')) update.end = DateTime.fromJSDate(oldEntity.end);

      update.name = this.generateName(update);
    }
  }

  private static generateName(examDay: ExamDay) {
    return `${examDay.start.toFormat('dd.LL.yyyy')}, ${examDay.start.toLocaleString(
      DateTime.TIME_24_SIMPLE,
    )} - ${examDay.end.toLocaleString(DateTime.TIME_24_SIMPLE)}`;
  }

  getAllocatedTesters() {
    return this.osceTesterTuples.flatMap(tuple => tuple.testerIDs).filter(Boolean).length;
  }

  getMaxTesters() {
    const nonTextTuples = this.osceTesterTuples.filter(tuple => tuple.osce?.taskType !== 'text');

    return nonTextTuples.length * this.calculateRoundsWithinExamDay().rounds;
  }

  allResultsSent(): 'all' | 'none' | 'some' {
    const candidates = this.candidates;
    if (!candidates?.length) return 'none';

    const resultsEmailStates = candidates
      .map(candidate => candidate.profile?.resultsEmailState)
      .map(resultMailState =>
        resultMailState?.filter(
          state => state.order.products.filter(prod => prod.product.reference === this._id).length,
        ),
      )
      .filter(resultMailStates => resultMailStates?.length);

    if (resultsEmailStates.length === candidates.length) return 'all';
    return resultsEmailStates.length ? 'some' : 'none';
  }

  allCertificatesSent(): 'all' | 'none' | 'some' {
    const candidates = this.candidates;
    if (!candidates?.length) return 'none';

    const certificateEmailStates = candidates
      .map(candidate => candidate.profile?.certificateEmailState)
      .map(certificateMailState =>
        certificateMailState?.filter(
          state => state.order.products.filter(prod => prod.product.reference === this._id).length,
        ),
      )
      .filter(certificateMailStates => certificateMailStates?.length);

    if (!certificateEmailStates.length) return 'none';
    if (certificateEmailStates.length === this.numPassedUsers && this.numRatedUsers === candidates.length) return 'all';
    return 'some';
  }

  @Query(type => ExamDay)
  public static getCurrentExamDay() {
    return async (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      const collection = await getCollection(ExamDay._class);
      const now = new Date();
      const selector = { _class: { $in: this.validSubclasses() }, end: { $gt: now } };
      return (
        (await collection
          .find(selector, {
            sort: { start: 1 },
          })
          .toArray()) as ExamDay[]
      )[0];
    };
  }

  static roundCalculationSelector = {
    osceTesterTuples: {
      _class: 1,
      osceID: 1,
      testerIDs: 1,
      osceGroupID: 1,
      osceGroupIndex: 1,
      osce: {
        taskType: 1,
        durationInMinutes: 1,
      },
    },
    fallbackTesters: 1,
    blockPause: 1,
    breakAfterRounds: 1,
    breakInMinutes: 1,
    slotPause: 1,
    start: 1,
    end: 1,
  } as const;

  calculateRoundsWithinExamDay(osces?: OSCE[]) {
    const defaultValue = {
      rounds: 0,
      timeline: [],
    };

    if (!this.has('osceTesterTuples')) return defaultValue;

    // all numbers are in minutes
    const start = this.start.toMillis() / 60000;
    const end = this.end.toMillis() / 60000;
    // const examDuration = end - start;
    // Anzahl der Slots pro Runde
    const slotsPerRound = this.osceTesterTuples.length || 1;
    // Wechselzeit zwischen Runden
    const blockPause = this.blockPause;
    // Dauer einer Mittagspause (alle X Runden)
    const lunchDuration = this.breakInMinutes;
    // Mittagspausen alle (X: lunchInterval) Runden
    const lunchInterval = this.breakAfterRounds;
    // Pause zwischen Slots
    const slotPause = this.slotPause;

    // Dauer aller Slots
    const slotDuration = this.osceTesterTuples.reduce((sum, tuple: OsceTesterTuple) => {
      const osce = osces ? osces.find(osce => osce._id === tuple.osceID) : tuple.osce;
      sum += osce?.durationInMinutes ?? (osce?.taskType === 'text' ? 20 : 10);
      return sum;
    }, 0);

    const slotPauseRoundDuration = slotPause * (slotsPerRound - 1);

    const roundDuration = slotDuration + slotPauseRoundDuration;

    if (start + roundDuration > end) return defaultValue;

    let rounds = 1;

    const timeline: TimelineItem[] = [];

    for (let currentDuration = start; currentDuration < end; rounds++) {
      const startOfRound = currentDuration;

      currentDuration += roundDuration;

      timeline.push({ type: TimelimeItemType.ROUND, start: startOfRound, end: currentDuration });

      let canAddRound = false;

      const startOfPause = currentDuration;

      if (lunchInterval > 0 && rounds % lunchInterval === 0 && currentDuration + lunchDuration + roundDuration <= end) {
        currentDuration += lunchDuration;

        canAddRound = true;
        timeline.push({ type: TimelimeItemType.LUNCH_PAUSE, start: startOfPause, end: currentDuration });
      } else if (
        !(lunchInterval > 0 && rounds % lunchInterval === 0) &&
        currentDuration + blockPause + roundDuration <= end
      ) {
        currentDuration += blockPause;

        canAddRound = true;
        timeline.push({ type: TimelimeItemType.BLOCK_PAUSE, start: startOfPause, end: currentDuration });
      }

      if (!canAddRound) {
        break;
      }

      if (currentDuration > end) {
        break;
      }
    }

    // normalize back to milliseconds
    timeline.forEach(item => {
      item.start = item.start * 60000;
      item.end = item.end * 60000;
    });

    return { rounds, timeline };
  }

  generateTesterArrays() {
    if (!this.has('osceTesterTuples')) return;

    const rounds = this.calculateRoundsWithinExamDay().rounds;

    this.fallbackTesters = Array.from({ length: rounds })
      .fill(null)
      .map((entry, index) => {
        return this.fallbackTesters[index] ?? null;
      });

    for (const tuple of this.osceTesterTuples) {
      tuple.testerIDs = Array.from({ length: rounds })
        .fill(null)
        .map((entry, index) => {
          return tuple.testerIDs?.[index] ?? null;
        });
    }
  }

  isAllowedToFallbackTest = (user: ILSUser): boolean => {
    const canHandleAllOsceTypes = this.osceTesterTuples.every(tuple => {
      if (tuple.osce?.taskType === 'text') return true;

      return tuple.osce?.osceType && user.assignedOSCETypes?.includes(tuple.osce.osceType);
    });

    return canHandleAllOsceTypes;
  };

  getAvailableRounds() {
    const rounds = this.generateRoundData();
    return rounds.filter(r => !r.fallbackTesterID || r.info.some(info => !info.testerID));
  }

  getAvailableRoundsForTester(tester: ILSUser) {
    const rounds = this.generateRoundData(tester);
    return rounds.filter(r => !r.fallbackTesterID || r.info.some(info => !info.testerID && info.allowed));
  }

  generateRoundData(tester?: ILSUser): Round[] {
    const roundsMeta = this.calculateRoundsWithinExamDay();
    const timeline = roundsMeta.timeline.filter(t => t.type === TimelimeItemType.ROUND);

    if (roundsMeta.rounds !== timeline.length)
      throw new Error(
        `Round calculation was incorrect. Rounds: ${roundsMeta.rounds}, Timeline: ${roundsMeta.timeline.length}`,
      );
    const rounds: Round[] = timeline.map((timeline, index) => {
      const round: Round = {
        round: index,
        timeline,
        info: [],
        fallbackTesterID: this.fallbackTesters[index] ?? null,
      };
      this.osceTesterTuples.forEach(tuple => {
        let testerID: string | null = null;
        if (!tuple.testerIDs) {
          testerID = null;
        } else {
          testerID = tuple.testerIDs[index] ?? null;
        }
        const isAllowed = tuple.osce?.osceType
          ? tester?.assignedOSCETypes?.includes(tuple.osce.osceType) ?? false
          : false;
        const roundInfo: RoundInfo = {
          testerID,
          osceID: tuple.osceID,
          allowed: isAllowed,
        };
        round.info.push(roundInfo);
      });
      return round;
    });
    return rounds;
  }

  getBookedRoundsForTester(tester: ILSUser) {
    const rounds = this.generateRoundData(tester);
    const bookedRounds = rounds.filter(round => round.info.some(info => info.testerID === tester._id));
    return bookedRounds;
  }

  private _isSlotInRound(slot: Slot, round: Round, index: number = 0, allRounds: Round[] = [round]) {
    const slotStart = slot.startTime.toMillis();
    const slotEnd = slot.endTime?.toMillis();
    // fallback slots are outside every round, so we extends the permissable times to the start of the next round.
    // this is 0 if not present because we test for the start of the slot being larger than this
    const startNextRound = allRounds[index + 1]?.timeline.start ?? Infinity;
    if (slot.isFallbackSlot) return round.timeline.start <= slotEnd && startNextRound >= slotStart;
    return round.timeline.start <= slotEnd && round.timeline.end >= slotStart;
  }

  getRoundNumberForSlot(slot: Slot) {
    const rounds = this.generateRoundData();
    const roundIndex = rounds.findIndex((round, index, allRounds) =>
      this._isSlotInRound(slot, round, index, allRounds),
    );
    return roundIndex;
  }

  slotIsInRound(roundNumber: number, slot: Slot) {
    const rounds = this.generateRoundData();
    const round = rounds[roundNumber];
    if (!round) throw new Error("roundNumber doesn't exist on exam day: " + roundNumber);
    return this._isSlotInRound(slot, round);
  }

  @Subscription(type => [ExamDay])
  public static subscribeExamDaysForCurrentUser() {
    return (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      if (context.serverContext) return of([]);

      return from(
        (async () => {
          const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
          if (!user) throw new Error('No user found');
          const pbf = await publishFilter(this._class);
          if (!pbf) throw new Error('No publish filter defined for ExamDay, please define a publish filter');
          return pbf;
        })(),
      ).pipe(
        switchMap(selector =>
          getCollectionHandle<ExamDay>(ExamDay._collectionName).watchQuery(selector, {
            sort: { start: 1 },
          }),
        ),
      );
    };
  }

  @Query(type => [ExamDay])
  public static getExamDayByCandidateID(@Arg('candidateIDs', type => [ID]) candidateIDs: string[]) {
    return async (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      const collection = await getCollection(ExamDay._class);
      const selector = {
        _class: { $in: this.validSubclasses() },
        candidateIDs: { $in: candidateIDs },
      };
      return collection
        .find(selector, {
          sort: { start: 1 },
        })
        .toArray() as Promise<ExamDay[]>;
    };
  }

  /**
   * Check if there are free spaces for candidates in an ExamDay.
   * This function expects { maxCandidates: 1 candidateIDs: 1} in the GraphQL Set
   */
  public async hasFreeSpaces() {
    if (!this.maxCandidates) return undefined;
    if (this.maxCandidates > this.candidateIDs.length) return true;
    else return false;
  }

  @Mutation(type => String)
  public static createNewExamDay(@Arg('examType', type => String) examType: ExamType) {
    return async () => {
      const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
      if (!user || !hasPermission(user, 'examdays', 'create')) throw new Error('No permissions to create an ExamDay');
      // TODO location handling
      const examCenterRole = user.roles.find(
        role => role.name === UserRole.EXAMCENTER || role.name === UserRole.EXAMCENTERMANAGER,
      );
      const lengthOfDay = 480;
      const dayStart = DateTime.now().plus({ days: 7 }).set({ hour: 10 }).startOf('hour');
      const dayEnd = dayStart.plus({ minute: lengthOfDay }).plus({ hour: 1 });

      const examDayData: PartialEntityData<ExamDay> = {
        examType: Object.values(ExamType).includes(examType) ? examType : ExamType.FAMED,
        start: dayStart,
        end: dayEnd,
        breakAfterRounds: 3,
        breakInMinutes: 60,
        candidateIDs: [],
        prices: [new Price({ unitAmount: 49000 })],
      };

      if (examCenterRole) {
        examDayData.locationID = examCenterRole.contexts[0];
        // examDayData.examCenter = [user._id];
      }

      const examDay = new ExamDay(examDayData);
      const examDayID = await examDay.create();
      return examDayID;
    };
  }

  @Mutation(type => String)
  public static copyExamDay(@Arg('examDayID', type => String) examDayID: string) {
    return async () => {
      const examDay = await getByID(this, examDayID)(this.allFields);
      if (!examDay) throw new Error(`examDay with id ${examDayID} not found.`);

      const copy = new ExamDay({
        ...examDay.toJSON(),
        _id: A.getGloballyUniqueID(),
        slotIDs: [],
        candidateIDs: [],
        start: examDay.start.plus({ days: 1 }),
        end: examDay.end.plus({ days: 1 }),
        osceTesterTuples: examDay.osceTesterTuples.map(tuple => ({ ...tuple.toJSON(), _id: A.getGloballyUniqueID() })),
        prices: examDay.prices.map(price => ({ ...price.toJSON(), _id: A.getGloballyUniqueID(), stripeID: null })),
      });
      return copy.create();
    };
  }

  @Mutation(type => Boolean)
  public static deleteSlotsOfExamDay(@Arg('examDayID', type => ID) examDayID: string) {
    return async () => {
      const examDayCollection = await getCollection<ExamDay>(ExamDay._class);
      const slotCollection = await getRawCollection<Slot>(Names.Slot._collectionName);
      const examDay = await examDayCollection.findOne({ _id: examDayID });
      if (!examDay) throw new Error(`no ExamDay with _id ${examDayID}`);

      if (!context.serverContext) {
        const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
        if (!hasPermission(user, 'slots', 'delete')) throw new Error('No permissions to delete slots');
      }
      const delResult = await slotCollection.deleteMany({ _id: { $in: examDay.slotIDs } });
      if (delResult.deletedCount !== examDay.slotIDs.length)
        throw new Error('ExamDay.deleteSlots(): Something went wrong when deleting the slots');

      const updateResult = await examDayCollection.updateOne({ _id: examDayID }, { $set: { slotIDs: [] } });
      if (updateResult.modifiedCount !== 1) throw new Error('ExamDay.deleteSlots(): failed to update examDay.slotIDs');

      return true;
    };
  }

  /**
   * This will extend the duration of the currently running ExamDay by the given time in minutes.
   *
   * **Important:** It will also shift **all** following slots to avoid overlaps and to ensure minimum break time.
   */
  @Mutation(type => Boolean)
  public static extendCurrentSlots(
    @Arg('examDayID', type => String) examDayID: string,
    @Arg('minutes', type => Int) minutes: number,
  ) {
    return async () => {
      if (!context.serverContext) {
        const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
        if (!hasPermission(user, 'slots', 'extend')) throw new Error('No permissions to extend slots');
      }

      const examDay = await getByID(ExamDay, examDayID)({ slotIDs: 1 });
      if (!examDay) throw new Error('ExamDay konnte nicht gefunden werden.');

      const slots = await Slot.getByID<Slot>(examDay.slotIDs)({ startTime: 1, endTime: 1 });
      if (slots.length !== examDay.slotIDs.length)
        throw new Error('Ein oder mehrere Slots konnten nicht gefunden werden.');

      // get running slots
      const currentSlots = slots.filter(
        slot => slot.startTime < DateTime.now() && (slot.endTime ? slot.endTime : Infinity) > DateTime.now(),
      );

      // get future slots
      const futureSlots = slots.filter(slot => slot.startTime > DateTime.now());

      // update dates
      const slotColl = await getRawCollection<Slot>(Names.Slot._collectionName);
      await withTransaction(async session => {
        await slotColl.updateMany(
          { _id: { $in: currentSlots.map(slot => slot._id) } },
          [{ $set: { endTime: { $dateAdd: { startDate: '$endTime', unit: 'minute', amount: minutes } } } }],
          { session },
        );
        await slotColl.updateMany(
          { _id: { $in: futureSlots.map(slot => slot._id) } },
          [
            {
              $set: {
                endTime: { $dateAdd: { startDate: '$endTime', unit: 'minute', amount: minutes } },
                startTime: { $dateAdd: { startDate: '$startTime', unit: 'minute', amount: minutes } },
              },
            },
          ],
          { session },
        );
      });

      return true;
    };
  }

  /**
   * @return true - if all mail were sent successfully
   */
  @Mutation(type => Boolean)
  public static sendRatingResultMails(
    @Arg('examDayID', type => String) examDayID: string,
    @Arg('ratedTraineeIDs', type => [String]) ratedTraineeIDs: string[],
  ) {
    return async () => {
      if (context.serverContext || !context.userID) throw new Error('Es scheint, dass kein Nutzer angemeldet ist.');
      const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
      if (!hasPermission(user, 'results', 'sendmail')) throw new Error('Du darfst das nicht machen.');

      const examDay = await getByID(
        ExamDay,
        examDayID,
      )({
        _id: 1,
        start: 1,
        end: 1,
        candidateIDs: 1,
        examType: 1,
        candidates: {
          _id: 1,
          profile: { _class: 1, resultsEmailState: { _class: 1, resultID: 1, orderID: 1 } },
        },
      });
      if (!examDay) throw new Error('ExamDay konnte nicht gefunden werden.');
      if (!examDay.candidates) throw new Error('ExamDay.candidates konnte nicht gefunden werden.');

      const orders = await getOrdersByExamDayAndTrainees(
        examDayID,
        ratedTraineeIDs,
      )({ _id: 1, isExamDayRated: 1, userReference: { userID: 1 } });

      orders.forEach(order => {
        if (!order.isExamDayRated) throw new Error(`Tried to send RatingResultMails for unrated order: ${order._id}`);
      });

      const candidatesWithoutResult = examDay.candidates.filter(candidate => {
        const order = orders.find(order => order.userReference.userID === candidate._id);
        if (!order) {
          logger.warn(`Could not find order for user ${candidate._id}`);
          return false;
        }
        return !candidate.resultMailSent(order._id);
      });

      const emailResults = await Promise.all(
        candidatesWithoutResult.map(candidate => sendRatingResultMail(examDay, candidate._id)),
      );

      if (!emailResults.every(Boolean)) return false;
      /** [Daniel-Todo] better error handling when some candidates fail */

      return true;
    };
  }

  @Mutation(type => Boolean)
  static sendRatingResultMail(@Arg('orderID', type => ID) orderID: string) {
    return async () => {
      // Check Session
      if (context.serverContext || !context.userID) throw new Error('Es scheint, dass kein Nutzer angemeldet ist.');

      // get ContextUser
      const contextUser = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
      if (!hasPermission(contextUser, 'results', 'sendmail')) throw new Error('Du darfst das nicht machen.');

      // get Order
      const order = await (await getCollection<Order>(Order._class)).findOne({ _id: orderID });
      if (!order) throw new Error('Die Order mit der ID ' + orderID + ' konnte nicht gefunden werden');

      // get User (from Order)
      const user = await (
        await getCollection<ILSUser>(ILSUser._class)
      ).findOne({ _id: order.userReference.userID ?? '__nothing__' });
      if (!user) throw new Error('Konnte keinen Nutzer zur Order ' + orderID + ' finden!');

      // get ExamDay
      const examDay = await (
        await getCollection<ExamDay>(ExamDay._class)
      ).findOne({ _id: order.products[0]?.product.reference });
      if (!examDay) throw new Error('In der Order ' + orderID + 'ist kein valider ExamDay verlinkt');

      // Send Mail
      await sendRatingResultMail(examDay, user._id);
    };
  }

  /**
   * @returns all ExamDays sorted by 'start' in ascending order
   */
  @Subscription(type => [ExamDay])
  public static subscribeAllExamDays() {
    return (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      return from(publishFilter(this._class)).pipe(
        switchMap(filter => {
          if (!filter) throw new Error('No publish filter defined for ExamDay, please define a publish filter');
          return watchQuery<ExamDay>(Names.ExamDay._collectionName, filter, { sort: { start: 1 } });
        }),
      );
    };
  }

  /**
   * @returns all published ExamDays that arent over yet, sorted by 'start' in ascending order
   */
  @Subscription(type => [ExamDay])
  public static subscribeAllPublishedExamDays(@Arg('examType', type => String) examType?: ExamType) {
    return (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      return from(publishFilter(this._class)).pipe(
        switchMap(pF => {
          if (!pF) throw new Error('No publish filter defined for ExamDay, please define a publish filter');
          return watchQuery<ExamDay>(
            this._collectionName,
            {
              $and: [
                pF,
                { published: true, start: { $gte: DateTime.now().startOf('day').plus({ day: 1 }).toJSDate() } },
                ...(examType ? [{ examType }] : []),
              ],
            },
            { sort: { start: 1 } },
          );
        }),
      );
    };
  }

  @Query(type => Int)
  static countExamdays(@Arg('filter', type => ExamDayFilter) filter: ExamDayFilter) {
    return async () => {
      const col = await getCollection<ExamDay>(this._class);
      return col.countDocuments(filter.getMongoFilter());
    };
  }

  @Subscription(type => [ExamDay])
  public static subscribeExamDayByFilter(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => ExamDayFilter) filter: ExamDayFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      return from(getRawCollection<EntityData<ExamDay>>(this._collectionName)).pipe(
        switchMap(async productsCol => {
          const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
          const isAdmin = hasGlobalRole(user, UserRole.ADMIN);
          const pF = isAdmin ? {} : await publishFilter(this._class);
          const result = await productsCol
            .aggregate<
              EntityData<ExamDay> & {
                scores?: { status: ScoreStatus }[];
                slots?: { candidateID: string; isCandidateMissing: boolean }[];
              }
            >([
              // match examdays
              { $match: { $and: [pF, filter.getMongoFilter()] } },
              { $sort: filter.getMongoOptions().sort },
              { $skip: skip },
              { $limit: limit },

              ...(gqlFields.numRatedUsers
                ? [
                    // add score data to determine rated user count
                    {
                      $lookup: {
                        from: Names.Score._collectionName,
                        as: 'scores',
                        localField: '_id',
                        foreignField: 'examDayID',
                        pipeline: [{ $project: { status: 1 } }],
                      },
                    },
                  ]
                : []),
              ...(gqlFields.numParticipatedUsers
                ? [
                    // add slot data data to determine participated user count
                    {
                      $lookup: {
                        from: Names.Slot._collectionName,
                        as: 'slots',
                        localField: 'slotIDs',
                        foreignField: '_id',
                        pipeline: [{ $project: { candidateID: 1, isCandidateMissing: 1 } }],
                      },
                    },
                  ]
                : []),
            ])
            .toArray();

          if (gqlFields.numParticipatedUsers || gqlFields.numRatedUsers) {
            return result.map(curr => {
              const rated = curr.scores?.reduce(
                (acc, score) => (score.status === 'failed' || score.status === 'succeeded' ? acc + 1 : acc),
                0,
              );
              const passed = curr.scores?.reduce((acc, score) => (score.status === 'succeeded' ? acc + 1 : acc), 0);
              const participated = curr.candidateIDs.filter(candidateID =>
                curr.slots.filter(slot => slot.candidateID === candidateID).every(slot => !slot.isCandidateMissing),
              ).length;
              return {
                ...curr,
                numRatedUsers: rated,
                numPassedUsers: passed,
                numParticipatedUsers: participated,
              };
            });
          }
          return result;
        }),
      );
    };
  }

  /**
   * @returns all ExamDays sorted by ascending dates, but with the structure of `[...futureExamDays, ...pastExamDays]`. Meaning the first entries are future exams sorted by ascending dates, followed by the past exams sorted by ascending dates
   */
  @Query(type => [ExamDay], { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } })
  public static getExamDaysSortedForSearch() {
    return async (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      const collection = await getCollection<ExamDay>(this._class);
      const examDays = await collection.find({ _class: this._class }).sort({ start: 1 }).toArray();

      const curDayIdx = examDays.findIndex(examDay => DateTime.fromJSDate(examDay.start) >= DateTime.now());
      examDays.splice(curDayIdx, 1);
      return examDays;
    };
  }

  @Query(type => [ExamDay])
  public static getFutureAndJoinedExamDays(
    @Arg('userID', type => String) userID: string,
    @Arg('examType', type => String) examType?: ExamType,
  ) {
    return async (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      const selector: any = { $or: [{ start: { $gt: new Date() } }, { candidateIDs: userID }] };
      if (examType) selector.examType = examType;
      const collection = await getCollection<ExamDay>(this._class);
      return collection.find(selector).sort({ start: 1 }).toArray() as unknown as Promise<ExamDay[]>;
    };
  }

  @Query(type => [ExamDay])
  public static getFutureAndBookedExamDays(@Arg('userID', type => String) userID: string) {
    return async (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      const collection = await getCollection<ExamDay>(this._class);
      return collection
        .find({
          $and: [
            { start: { $gt: new Date() } },
            { $or: [{ fallbackTesters: userID }, { 'osceTesterTuples.testerIDs': userID }] },
          ],
        })
        .toArray() as unknown as Promise<ExamDay[]>;
    };
  }

  @Query(type => [ExamDay])
  static getFutureExamDaysPublishedForTesters() {
    return async (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      const collection = await getCollection<ExamDay>(this._class);
      return collection
        .find({
          start: { $gt: new Date() },
          isPublishedForTesters: true,
          archived: false,
        })
        .sort({ start: 1 })
        .toArray() as unknown as Promise<ExamDay[]>;
    };
  }

  @Query(type => [ExamDay])
  static getExamDaysWithOsce(@Arg('osceID', type => ID) osceID: string) {
    return async (gqlFields: BaseQLSelectionSet<ExamDay>) => {
      return (await getCollection(this._class)).find({ 'osceTesterTuples.osceID': osceID }).toArray() as Promise<
        ExamDay[]
      >;
    };
  }

  @Mutation(type => Boolean)
  public static updateMissingCandidates(
    @Arg('examDayID', type => String) examDayID: string,
    @Arg('candidateIDs', type => [String]) candidateIDs: string[],
  ) {
    return async () => {
      if (!context.serverContext) {
        const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
        if (!hasPermission(user, 'examdays', 'modifymissingcandidates'))
          throw new Error('No permission to update missing candidates');
      }

      // We get the slotIDs that belong to the examDay, so that we later will only update them
      const examDay = await getByID(ExamDay, examDayID)({ candidateIDs: 1, slotIDs: 1 });
      if (!examDay) throw new Error(`Es wurde kein Prüfungstag unter der ID '${examDayID}' gefunden.`);

      const slots = await getRawCollection<Slot>(Names.Slot._collectionName);
      // update not missing candidates
      const notMissing = examDay.candidateIDs.filter(c => !candidateIDs.includes(c));
      await slots.updateMany(
        {
          candidateID: { $in: notMissing },
          _id: { $in: examDay.slotIDs },
        },
        { $set: { isCandidateMissing: false } },
      );

      // update missing canididates
      const missing = examDay.candidateIDs.filter(c => candidateIDs.includes(c));
      await slots.updateMany(
        {
          candidateID: { $in: missing },
          _id: { $in: examDay.slotIDs },
        },
        { $set: { isCandidateMissing: true } },
      );
    };
  }

  getUsableSlotsForCandidate(candidateID: string) {
    if (!this.slots.length) throw new Error('Slots are not ready or there are no slots.');
    return this.slots.filter(slot => slot.candidateID === candidateID).filter(slot => !slot.fallbackSlotID);
  }

  @Mutation(type => String)
  static scheduleTestExamDayInMinutes(
    @Arg('inMinutes', type => Int) inMinutes: number,
    @Arg('shortOrRegular', type => Int) shortOrRegular: number,
    @Arg('jobID', type => String) jobID: string,
  ) {
    return async () => {
      if (!shortOrRegular) throw new Error('shortOrRegular not set on scheduleTestExamDayInMinutes');
      if (baseConfig.get('DEPLOYMENT') === 'production') throw new Error('Cannot schedule test examday in production');

      const newExamDay = await new Promise<ExamDay>(resolve => {
        processMap[jobID] = new Observable<string[]>(sub => {
          createTestExam(inMinutes, shortOrRegular, sub).then(examDay => {
            resolve(examDay);
          });
        });
      });

      return newExamDay._id;
    };
  }

  @Subscription(type => [String])
  static TestExamProgressSubscription(@Arg('jobID', type => String) jobID: string) {
    return () => {
      return processMap[jobID];
    };
  }

  @Mutation(type => Boolean)
  static sendCertificateMail(@Arg('orderID', type => ID) orderID: string) {
    return async () => {
      if (context.serverContext || !context.userID) throw new Error('Es scheint, dass kein Nutzer angemeldet ist.');

      // get context user
      const contextUser = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
      if (!hasPermission(contextUser, 'certificates', 'sendmail'))
        throw new Error('Nur Admins können Zertifikate versenden.');

      const order = await (async () => {
        const $o = await (await getCollection<Order>(Names.Order._class)).findOne({ _id: orderID });
        if (!$o) throw new Error(`Order mit ID ${orderID} nicht gefunden.`);
        return $o as unknown as Order;
      })();

      const user = await (async (userID: string) => {
        const $u = await (await getCollection<ILSUser>(Names.ILSUser._class)).findOne({ _id: userID });
        if (!$u) throw new Error(`User mit ID ${userID} nicht gefunden.`);
        return $u as unknown as ILSUser;
      })(order.userReference.userID ?? '__nothing__');

      const examDay = await (async (examDayID: string | undefined) => {
        const $e = await (await getCollection<ExamDay>(Names.ExamDay._class)).findOne({ _id: examDayID });
        if (!$e) throw new Error(`ExamDay mit ID ${examDayID} nicht gefunden.`);
        return $e as unknown as ExamDay;
      })(order.products[0]?.product.reference);

      const examDayString = ((d: DateTime | Date): string => {
        if (d instanceof DateTime) return d.toFormat('dd.MM.yyyy');
        if (d instanceof Date) return DateTime.fromJSDate(d).toFormat('dd.MM.yyyy');
        throw new Error('d is neither DateTime nor Date');
      })(examDay.start);

      await sendCertificate(order._id, examDayString, generateCertificatePdfFileName(user))();

      return true;
    };
  }
}
